import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import {
  ACCOUNT_ROUTES,
  APP_CONTAINER_ROUTES,
  APP_MODAL_ROUTES,
  CONTAINER_MODAL_ROUTES,
} from '@/navigators/routes';
import {
  ACCOUNT_SCREEN_WIDTH,
  RestaurantAccountComponent,
} from './restaurant-account-component';
import {
  AccountStackParams,
  AppContainerStackParams,
  AppModalStackParams,
  ContainerModalStackParams,
} from '@/navigators/types';
import { ActionSheet } from '@/components/action-sheet';
import { Button } from '@/components/button';
import { CompositeNavigationProp, RouteProp } from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import { DeleteAccountActionSheet } from './delete-account-action-sheet';
import { Disclosures } from '@/components/disclosures';
import {
  PageContainer,
  NonAnimatedProps as PageContainerProps,
} from '@/components/page-container';
import { partnerSupplier } from '@/utils/environment';
import { SCREEN_PADDING } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleProp, View, ViewStyle } from 'react-native';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import {
  useLayoutContext,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSignOut } from '@/hooks/use-sign-out';

type Navigation = CompositeNavigationProp<
  CompositeNavigationProp<
    StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES.Account>,
    StackNavigationProp<AppModalStackParams>
  >,
  StackNavigationProp<ContainerModalStackParams>
>;

interface Props {
  navigation: Navigation;
  route:
    | RouteProp<AppContainerStackParams, APP_CONTAINER_ROUTES.Account>
    | RouteProp<AccountStackParams, ACCOUNT_ROUTES.Base>;
}

const MAX_BOTTOM_PADDING = 16;

export const RestaurantAccountScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const signOut = useSignOut();
  const { bottom } = useSafeAreaInsets();
  const { user } = useAuthenticatedUserContext();
  const [styles] = useStyles();

  const [showAccountActionSheet, setShowAccountActionSheet] = useState(false);
  const [showAccountDeletionActionSheet, setShowAccountDeletionActionSheet] =
    useState(false);

  const { isSmallLayout } = useLayoutContext();
  const hideActions = route?.params?.hideActions;

  const pageContainerProps = useMemo<PageContainerProps>(() => {
    if (isSmallLayout) {
      return {
        navigationHeaderProps: {
          centerTitle: true,
          showBackButton: true,
          title: i18n.t('my_account'),
        },
      };
    }

    return {
      showNavigationHeader: false,
    };
  }, [isSmallLayout]);

  const contentStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(
    styles.content,
    [styles.content, styles.contentLarge],
  );

  const footerStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(
    [styles.footer, styles.footerBorder],
    styles.footer,
  );

  const navigateToUpdateAccount = () => {
    navigation.navigate(APP_MODAL_ROUTES.UpdateEmployee, {
      contactProtected: false,
      employeeUUID: user.uuid,
      initialEmail: user.contact_info.email ?? null,
      initialLoginEmail: user.contact_info.login_email ?? null,
      initialName: user.name,
      initialPhoneNumber: user.contact_info.phone_number,
      title: i18n.t('update_account'),
    });
  };

  return (
    <PageContainer {...pageContainerProps}>
      <View style={contentStyle}>
        <RestaurantAccountComponent
          contactEmail={user.contact_info.email}
          contactPhoneNumber={user.contact_info.phone_number}
          containerStyle={styles.accountListStyle}
          employeeName={user.name}
          employeeUUID={user.uuid}
          ListFooterComponent={
            isSmallLayout && !hideActions ? (
              <Button
                onPress={async () => {
                  await signOut();
                }}
                style={styles.logoutButton}
                trackingData={{
                  name: 'account_screen.logout_button',
                }}
                type="secondary"
              >
                {i18n.t('log_out')}
              </Button>
            ) : (
              <>
                <Button
                  onPress={navigateToUpdateAccount}
                  style={styles.updateAccountButton}
                  trackingData={{
                    name: 'account_screen.update_account_button',
                  }}
                >
                  {i18n.t('update_account')}
                </Button>
                <Button
                  onPress={() => {
                    setShowAccountDeletionActionSheet(true);
                  }}
                  trackingData={{
                    name: 'account_screen.delete_account_button',
                  }}
                  type="danger"
                >
                  {i18n.t('delete_account')}
                </Button>
              </>
            )
          }
          loginEmail={user.contact_info.login_email}
          onAvatarPress={
            partnerSupplier.demoEnabled
              ? () => {
                  navigation.navigate(CONTAINER_MODAL_ROUTES.DemoSettings);
                }
              : undefined
          }
          onPressNotifications={() => {
            navigation.navigate(
              isSmallLayout
                ? APP_CONTAINER_ROUTES.AccountNotificationSettings
                : APP_MODAL_ROUTES.AccountNotificationSettings,
              {
                userUUID: user.uuid,
              },
            );
          }}
        />
      </View>
      {hideActions ? null : (
        <View
          style={[
            footerStyle,
            {
              paddingBottom: Math.max(bottom, MAX_BOTTOM_PADDING),
            },
          ]}
        >
          <Disclosures />
          {showAccountActionSheet ? (
            <ActionSheet
              actions={[i18n.t('update_account'), i18n.t('delete_account')]}
              dangerIndices={[1]}
              isVisible
              onAction={(_, index) => {
                if (index === 0) {
                  Analytics.track(
                    'restaurant_account.update_profile_button.press',
                  );
                  navigateToUpdateAccount();
                } else if (index === 1) {
                  Analytics.track(
                    'restaurant_account.delete_account_button.press',
                  );
                  setShowAccountDeletionActionSheet(true);
                }

                setShowAccountActionSheet(false);
              }}
              onClose={() => setShowAccountActionSheet(false)}
            />
          ) : null}
        </View>
      )}
      {showAccountDeletionActionSheet ? (
        <DeleteAccountActionSheet
          onClose={() => setShowAccountDeletionActionSheet(false)}
        />
      ) : null}
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  accountListStyle: {
    paddingHorizontal: SCREEN_PADDING,
  },
  content: {
    flex: 1,
    justifyContent: 'space-between',
  },
  contentLarge: {
    alignSelf: 'center',
    marginHorizontal: 'auto',
    paddingHorizontal: 0,
    width: ACCOUNT_SCREEN_WIDTH,
  },
  footer: {
    paddingTop: 12,
  },
  footerBorder: {
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
  },
  logoutButton: {
    marginBottom: 32,
    marginTop: 8,
  },
  updateAccountButton: {
    marginBottom: 8,
    marginTop: SCREEN_PADDING,
  },
}));
